import {
  LiquidMetalShapes,
  ShaderFitOptions,
  ShaderMount,
  getShaderColorFromString,
  liquidMetalFragmentShader,
  toProcessedLiquidMetal,
} from '@theme/paper-liquid-metal';

const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(maximum, Math.max(minimum, number)) : fallback;
};

class LiquidMetalImage extends HTMLElement {
  constructor() {
    super();
    this.mount = null;
    this.observer = null;
    this.abortController = null;
    this.objectUrls = [];
    this.started = false;
    this.frame = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = this.onMotionChange.bind(this);
  }

  connectedCallback() {
    if (this.started || this.mount) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          this.observer?.disconnect();
          this.observer = null;
          this.start();
        },
        { rootMargin: '300px' }
      );
      this.observer.observe(this);
    } else {
      this.start();
    }

    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener('change', this.onMotionChange);
    } else {
      this.reducedMotion.addListener(this.onMotionChange);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.abortController?.abort();
    this.abortController = null;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.mount?.dispose();
    this.mount = null;
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
    this.started = false;

    if (this.reducedMotion.removeEventListener) {
      this.reducedMotion.removeEventListener('change', this.onMotionChange);
    } else {
      this.reducedMotion.removeListener(this.onMotionChange);
    }
  }

  onMotionChange() {
    if (!this.mount || !this.config) return;
    this.mount.setSpeed(this.reducedMotion.matches ? 0 : this.config.speed);
  }

  readConfig() {
    const node = this.querySelector('[data-liquid-metal-config]');
    if (!node) throw new Error('Liquid Metal configuration is missing.');
    const raw = JSON.parse(node.textContent || '{}');

    return {
      image: typeof raw.image === 'string' ? raw.image : '',
      colorBack: raw.colorBack || '#a7a7aa',
      colorTint: raw.colorTint || '#ffffff',
      shape: LiquidMetalShapes[raw.shape] ?? LiquidMetalShapes.none,
      repetition: clamp(raw.repetition, 1, 10, 2.5),
      softness: clamp(raw.softness, 0, 1, 0.15),
      shiftRed: clamp(raw.shiftRed, -1, 1, 0.15),
      shiftBlue: clamp(raw.shiftBlue, -1, 1, 0.2),
      distortion: clamp(raw.distortion, 0, 1, 0.12),
      contour: clamp(raw.contour, 0, 1, 0.65),
      angle: clamp(raw.angle, 0, 360, 70),
      speed: clamp(raw.speed, -4, 4, 1),
      frame: clamp(raw.frame, 0, 9900, 0),
      fit: ShaderFitOptions[raw.fit] ?? ShaderFitOptions.contain,
      scale: clamp(raw.scale, 0.05, 4, 0.85),
      rotation: clamp(raw.rotation, 0, 360, 0),
      offsetX: clamp(raw.offsetX, -1, 1, 0),
      offsetY: clamp(raw.offsetY, -1, 1, 0),
      originX: clamp(raw.originX, 0, 1, 0.5),
      originY: clamp(raw.originY, 0, 1, 0.5),
      worldWidth: clamp(raw.worldWidth, 0, 5000, 0),
      worldHeight: clamp(raw.worldHeight, 0, 5000, 0),
      minPixelRatio: clamp(raw.minPixelRatio, 0.5, 3, 1.5),
      maxPixelCount: clamp(raw.maxPixelCount, 100000, 12000000, 3000000),
    };
  }

  async waitForTarget(target) {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      if (!this.isConnected) throw new DOMException('Block removed', 'AbortError');
      const bounds = target.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) return;
      await new Promise((resolve) => {
        this.frame = requestAnimationFrame(resolve);
      });
    }
    throw new Error('Liquid Metal target has no visible size.');
  }

  async prepareImage(url) {
    if (!url) return null;
    this.abortController = new AbortController();
    const response = await fetch(url, { mode: 'cors', signal: this.abortController.signal });
    if (!response.ok) throw new Error(`Logo image failed to load (${response.status}).`);

    const blob = await response.blob();
    const sourceUrl = URL.createObjectURL(blob);
    this.objectUrls.push(sourceUrl);
    const file = new File([blob], 'harper-slein-logo', { type: blob.type || 'image/png' });
    const processed = await toProcessedLiquidMetal(file);
    const processedUrl = URL.createObjectURL(processed.pngBlob);
    this.objectUrls.push(processedUrl);

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = processedUrl;
    await image.decode();
    return image;
  }

  async start() {
    if (this.started || !this.isConnected) return;
    this.started = true;
    this.removeAttribute('ready');
    this.removeAttribute('shader-error');

    try {
      this.config = this.readConfig();
      const target = this.querySelector('[data-liquid-metal-target]');
      if (!target) throw new Error('Liquid Metal target is missing.');
      await this.waitForTarget(target);
      const image = await this.prepareImage(this.config.image);
      if (!this.isConnected) return;

      const uniforms = {
        u_colorBack: getShaderColorFromString(this.config.colorBack),
        u_colorTint: getShaderColorFromString(this.config.colorTint),
        u_image: image || undefined,
        u_contour: this.config.contour,
        u_distortion: this.config.distortion,
        u_softness: this.config.softness,
        u_repetition: this.config.repetition,
        u_shiftRed: this.config.shiftRed,
        u_shiftBlue: this.config.shiftBlue,
        u_angle: this.config.angle,
        u_isImage: Boolean(image),
        u_shape: this.config.shape,
        u_fit: this.config.fit,
        u_scale: this.config.scale,
        u_rotation: this.config.rotation,
        u_offsetX: this.config.offsetX,
        u_offsetY: this.config.offsetY,
        u_originX: this.config.originX,
        u_originY: this.config.originY,
        u_worldWidth: this.config.worldWidth,
        u_worldHeight: this.config.worldHeight,
      };

      this.mount = new ShaderMount(
        target,
        liquidMetalFragmentShader,
        uniforms,
        { alpha: true, antialias: true, premultipliedAlpha: true },
        this.reducedMotion.matches ? 0 : this.config.speed,
        this.config.frame,
        this.config.minPixelRatio,
        this.config.maxPixelCount,
        image ? ['u_image'] : []
      );
      this.setAttribute('ready', '');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.setAttribute('shader-error', '');
      console.warn('Harper Liquid Metal:', error);
    }
  }
}

if (!customElements.get('liquid-metal-image')) {
  customElements.define('liquid-metal-image', LiquidMetalImage);
}
